import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Lock, ShieldCheck } from "@phosphor-icons/react";

/**
 * Full-screen block shown right after login when the user was seeded with a
 * temporary password (production admin bootstrap). Cannot be dismissed until
 * the user sets a new strong password.
 */
export default function ForcePasswordChange() {
  const { user, refresh, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 8) { setErr("New password must be at least 8 characters"); return; }
    if (next !== confirm) { setErr("Confirmation does not match"); return; }
    if (next === current) { setErr("New password must be different from current"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed. Continuing to app…");
      await refresh();
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message || "Change failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6" data-testid="force-password-change">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2 text-slate-900">
          <ShieldCheck size={22} className="text-sky-600" />
          <h2 className="text-lg font-semibold">Set a new password to continue</h2>
        </div>
        <p className="text-sm text-slate-500">
          Your account (<b>{user?.email}</b>) was created with a temporary password. Choose a new one before using KGS SDMS.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Current (temporary) password</label>
            <div className="relative mt-1">
              <Lock size={14} className="absolute left-3 top-3 text-slate-400" />
              <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
                     data-testid="fpc-current"
                     className="w-full border border-slate-200 rounded-md pl-9 pr-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">New password (min 8 chars)</label>
            <input type="password" required value={next} onChange={(e) => setNext(e.target.value)}
                   data-testid="fpc-new"
                   className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm mt-1" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Confirm new password</label>
            <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                   data-testid="fpc-confirm"
                   className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm mt-1" />
          </div>
          {err && <div className="text-sm text-red-600" data-testid="fpc-error">{err}</div>}
          <div className="flex items-center justify-between pt-2">
            <button type="button" onClick={logout} data-testid="fpc-signout"
                    className="text-xs text-slate-500 hover:text-slate-900 underline">Sign out</button>
            <button type="submit" disabled={busy} data-testid="fpc-submit"
                    className="bg-sky-600 hover:bg-sky-700 disabled:opacity-70 text-white text-sm font-medium px-4 py-2 rounded-md">
              {busy ? "Saving…" : "Change password & continue"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
